import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { BrandLogo } from "@/components/brand-logo"
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion"
import { cn } from "@/lib/utils"

/**
 * OKS-457 — §00 immersive brand splash overlay (UX Spec §2 / PRD §1).
 *
 * A full-bleed, first-view-only load mask that paints the brand's splash
 * background + scrim + logo lockup, holds for a minimum beat, then
 * **cross-dissolves** to reveal §01 (already painted beneath it in the same
 * Inertia page — TRD KD-1: overlay, not a route). The parent gates the
 * mount on `show_splash && state === "valid"` and removes it on
 * `onComplete`.
 *
 * # Lifecycle (never deadlocks)
 *
 *   mount → [min-hold timer ~600ms] ┐
 *           [cover loaded OR ~2.5s   ├─ both ready → dissolve (~400ms) → onComplete
 *            safety timeout]        ┘
 *
 * The cover-settle safety timeout guarantees advance even if the proxied
 * background image is slow or missing (PRD §6 load-durability) — a brand
 * with no background settles immediately (`backgroundUrl == null`).
 *
 * # Background precedence (OKS-857)
 *
 * The background source is `splashCoverUrl ?? coverUrl`: the brand's
 * portrait 9:16 splash image when set, else the landscape cover, else the
 * dark backdrop. This single derived source drives ONE `<img>` + ONE
 * settle path — the portrait/cover choice is made at the prop layer, so
 * the lifecycle machinery below is unchanged from what shipped.
 *
 * # Invite-scoped opt-in (OKS-738 / OKS-857)
 *
 * Two optional props extend the splash for the INVITE surface only, while
 * the brand-switch surface stays byte-identical to today (TRD KD-6):
 *   - `minHoldMs` — longer deliberate hold (invite passes 3500; default
 *     stays 600 for the switch). Also stretches the cover-settle safety
 *     ceiling, which is DERIVED (`max(2500, minHoldMs + 500)`) so the
 *     switch keeps its 2500ms ceiling untouched.
 *   - `taglines` — an already-picked, ordered list of crew-perk taglines
 *     (the random 2–3 pick lives in `pickTaglines`, called by
 *     `InviteLanding`, so this component stays deterministic). When
 *     present, a cross-fading tagline row is shown below the lockup; when
 *     ABSENT (switch surface), the 3 loading dots render instead.
 *
 * # Static logo (OKS-857)
 *
 * The logo lockup renders STATIC from first paint — no entrance beat and
 * no ring loader (both removed in OKS-857, partially reversing OKS-738's
 * ring). The logo card keeps its own `ring-1 ring-white/15` frame. The
 * reserved tagline row is positioned OUT of the centered flex flow
 * (absolute, below the lockup) so the logo card lands at the SAME optical
 * vertical center on both the invite (taglines mounted) and switch
 * (taglines absent) surfaces (UX Spec §2 centring fix).
 *
 * # Reduced motion (UX Spec §4 / §5)
 *
 * `usePrefersReducedMotion()` gates the React side: the cross-dissolve
 * transition is NOT mounted — we hold the min beat then call `onComplete`
 * for an instant swap. The logo is already static for everyone (the
 * entrance beat is gone), so there is nothing motion-specific left on the
 * lockup. On the invite surface exactly one tagline shows (no cross-fade)
 * but the full `minHoldMs` beat is still held so the user can read it.
 * Never strands.
 *
 * # Accessibility (UX Spec §6)
 *
 * The overlay is decorative chrome — `aria-hidden` so it never traps a
 * screen reader; §01 beneath is the announced content and auto-advance
 * means focus is never parked here.
 *
 * @param onComplete MUST be referentially stable (wrap in `useCallback`) —
 *   it's an effect dependency that drives the advance.
 */
export interface BrandSplashProps {
  brandName: string
  logoUrl: string | null
  coverUrl: string | null
  /**
   * OKS-857 — portrait (9:16) splash background. When present it takes
   * PRECEDENCE over `coverUrl` (`splashCoverUrl ?? coverUrl`); the resulting
   * single background source is full-bleed `object-cover` behind the same
   * dark scrim, and it reuses the shipped cover-settle safety timeout so a
   * missing/slow portrait never strands the splash. Omit (or null) → the
   * splash falls back to `coverUrl`, then the dark backdrop (unchanged).
   */
  splashCoverUrl?: string | null
  onComplete: () => void
  testId?: string
  /**
   * Invite-scoped (OKS-738): already-picked, ordered taglines to
   * cross-fade through. Omit on the switch surface — no tagline row
   * renders and the dots stay. Defaults to `undefined` (switch behavior).
   */
  taglines?: string[]
  /**
   * Invite-scoped (OKS-738): minimum on-screen hold. Defaults to
   * `SPLASH_MIN_HOLD_MS` (600) so the switch surface is unchanged; invite
   * passes 3500.
   */
  minHoldMs?: number
}

/** Minimum on-screen hold before advancing (PRD §3 "~600ms"). */
export const SPLASH_MIN_HOLD_MS = 600
/** Cross-dissolve duration into §01 (UX Spec §4 "~400ms"). */
export const SPLASH_DISSOLVE_MS = 400
/** Safety net: advance even if the background never loads (slow/missing). */
export const SPLASH_COVER_TIMEOUT_MS = 2500

/**
 * Per-tagline visible duration during the cross-fade rotation — the hold
 * split evenly across the picked taglines (UX Spec §4). Exported so tests
 * advance fake timers by the real cadence rather than a magic number.
 */
export function taglineIntervalMs(minHoldMs: number, count: number): number {
  return count > 0 ? Math.floor(minHoldMs / count) : minHoldMs
}

export function BrandSplash({
  brandName,
  logoUrl,
  coverUrl,
  splashCoverUrl,
  onComplete,
  testId = "brand-splash",
  taglines,
  minHoldMs = SPLASH_MIN_HOLD_MS,
}: BrandSplashProps) {
  const { t } = useTranslation()
  const reducedMotion = usePrefersReducedMotion()

  // OKS-857 — background precedence: portrait splash image → landscape
  // cover → dark backdrop. Resolved once here so a single `<img>` + a
  // single settle path drive the lifecycle (the portrait/cover choice is
  // upstream of all the timing machinery).
  const backgroundUrl = splashCoverUrl ?? coverUrl

  const hasTaglines = (taglines?.length ?? 0) > 0
  const taglineCount = taglines?.length ?? 0
  // Cover-settle ceiling DERIVED so it always sits above the hold without
  // changing the switch surface's 2500ms: switch (600) → 2500, invite
  // (3500) → 4000.
  const coverTimeoutMs = Math.max(SPLASH_COVER_TIMEOUT_MS, minHoldMs + 500)

  const [minHoldElapsed, setMinHoldElapsed] = useState(false)
  // No background → nothing to wait for; settle immediately.
  const [coverSettled, setCoverSettled] = useState(!backgroundUrl)
  // Active tagline index (cross-fade rotation). Stays 0 under reduced
  // motion / single tagline (UX Spec §5 static single tagline).
  const [activeTagline, setActiveTagline] = useState(0)
  // One-shot guard so the completion timer is scheduled exactly once.
  const advancedRef = useRef(false)

  const settleCover = useCallback(() => setCoverSettled(true), [])

  // Both gates cleared → time to advance. `dissolving` is DERIVED (not a
  // state set in an effect): under reduced motion we complete instantly
  // and never paint the dissolve, so it's gated off `reducedMotion` too.
  const ready = minHoldElapsed && coverSettled
  const dissolving = ready && !reducedMotion

  // Min-hold beat (invite passes a longer `minHoldMs`).
  useEffect(() => {
    const id = window.setTimeout(() => setMinHoldElapsed(true), minHoldMs)
    return () => window.clearTimeout(id)
  }, [minHoldMs])

  // Cover-settle safety timeout (only while still waiting on a background).
  // Ceiling is derived so it never trails the hold.
  useEffect(() => {
    if (coverSettled) return undefined
    const id = window.setTimeout(settleCover, coverTimeoutMs)
    return () => window.clearTimeout(id)
  }, [coverSettled, settleCover, coverTimeoutMs])

  // Tagline cross-fade rotation: advance the active index once per
  // `taglineIntervalMs`, stopping on the last tagline. Skipped under reduced
  // motion and for a single tagline (UX Spec §5 — one static tagline).
  useEffect(() => {
    if (!hasTaglines || reducedMotion || taglineCount <= 1) return undefined
    const interval = taglineIntervalMs(minHoldMs, taglineCount)
    let index = 0
    const id = window.setInterval(() => {
      index += 1
      if (index >= taglineCount) {
        window.clearInterval(id)
        return
      }
      setActiveTagline(index)
    }, interval)
    return () => window.clearInterval(id)
  }, [hasTaglines, reducedMotion, taglineCount, minHoldMs])

  // Advance once both gates clear. Reduced motion → instant `onComplete`;
  // otherwise let the derived cross-dissolve play, then complete after
  // `SPLASH_DISSOLVE_MS`. `advancedRef` makes this single-fire so a
  // re-render never re-schedules (or cancels) the completion timer.
  useEffect(() => {
    if (advancedRef.current || !ready) return undefined
    advancedRef.current = true

    if (reducedMotion) {
      onComplete()
      return undefined
    }

    const id = window.setTimeout(onComplete, SPLASH_DISSOLVE_MS)
    return () => window.clearTimeout(id)
  }, [ready, reducedMotion, onComplete])

  return (
    <div
      aria-hidden="true"
      data-testid={testId}
      data-state={dissolving ? "dissolving" : "visible"}
      className={cn(
        "fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden",
        // Concrete dark backdrop (matches the shipped invite-landing Hero)
        // so the splash is guaranteed dark even when no background loads.
        "bg-zinc-950 transition-opacity ease-out",
        dissolving ? "opacity-0" : "opacity-100",
      )}
      style={{ transitionDuration: `${SPLASH_DISSOLVE_MS}ms` }}
    >
      {backgroundUrl && (
        <img
          src={backgroundUrl}
          alt=""
          loading="eager"
          decoding="async"
          onLoad={settleCover}
          onError={settleCover}
          data-testid={`${testId}-cover`}
          className="absolute inset-0 size-full object-cover"
        />
      )}
      {/* Dark vignette scrim for lockup legibility over any background. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/70"
      />

      {/* Centred lockup — logo app-icon card → brand title → CREW eyebrow.
          Renders STATIC from first paint (OKS-857 — no entrance beat, no
          ring). `relative` anchors the absolutely-positioned tagline row so
          it sits BELOW the lockup without shifting the logo's optical
          center (equal on invite + switch surfaces). */}
      <div
        data-testid={`${testId}-lockup`}
        className="relative z-10 flex flex-col items-center gap-3 text-center text-white"
      >
        <BrandLogo
          logoUrl={logoUrl}
          name={brandName}
          className="size-[88px] rounded-[22px] bg-white/10 object-contain p-3 shadow-lg ring-1 ring-white/15"
          monogramClassName="text-3xl text-white"
          alt={t("invites.landing.brand_logo_alt", { brand_name: brandName })}
          testId={`${testId}-logo`}
        />
        <div className="flex flex-col items-center gap-1">
          <span
            data-testid={`${testId}-brand-name`}
            className="text-[22px] leading-tight font-semibold text-white"
          >
            {brandName}
          </span>
          <span className="text-[11px] font-medium tracking-[0.2em] text-white/70 uppercase">
            {t("invites.landing.splash.eyebrow")}
          </span>
        </div>

        {/* Rotating crew-perk taglines (OKS-738) — invite surface only.
            One visible at a time; entries stacked and cross-faded by
            opacity. The active entry carries the `-tagline` testId so it is
            singularly queryable. Height reserved for two lines to prevent
            layout jump between short/long entries (UX Spec §3).

            OKS-857 — positioned ABSOLUTELY below the lockup (`top-full`) so
            it is OUT of the centered flex flow: the logo+name unit centers
            identically whether or not taglines are mounted, so invite and
            switch land the logo at the same vertical position (UX Spec §2
            centring fix). */}
        {hasTaglines && (
          <div className="absolute top-full left-1/2 mt-2 flex min-h-[2.75rem] w-full max-w-xs -translate-x-1/2 items-start justify-center">
            {taglines!.map((line, index) => (
              <span
                key={index}
                data-testid={
                  activeTagline === index ? `${testId}-tagline` : undefined
                }
                className={cn(
                  "absolute inset-x-0 px-8 text-center text-sm font-medium text-white/80",
                  "line-clamp-2",
                  !reducedMotion &&
                    "transition-opacity duration-[350ms] ease-out",
                  activeTagline === index ? "opacity-100" : "opacity-0",
                )}
              >
                {line}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Progress-feel loading dots — switch surface only. The invite
          surface (OKS-738) shows rotating taglines instead. */}
      {!hasTaglines && (
        <div
          aria-hidden="true"
          data-testid={`${testId}-dots`}
          className="absolute bottom-16 flex items-center gap-1.5"
        >
          <span className="size-1.5 rounded-full bg-white/90" />
          <span className="size-1.5 rounded-full bg-white/40" />
          <span className="size-1.5 rounded-full bg-white/40" />
        </div>
      )}
    </div>
  )
}

export default BrandSplash
